import { useState, useEffect, useMemo, useCallback } from 'react'
import { useAppSelector } from './redux'
import { getApiUrl, createFetchConfig } from '../config/api'

// Permission enum (should match server-side)
export enum Permission {
  // System-level permissions
  MANAGE_USERS = 'MANAGE_USERS',
  MANAGE_SYSTEM_SETTINGS = 'MANAGE_SYSTEM_SETTINGS',
  VIEW_AUDIT_LOGS = 'VIEW_AUDIT_LOGS',
  DELETE_USERS = 'DELETE_USERS',
  
  // Project-level permissions
  CREATE_PROJECT = 'CREATE_PROJECT',
  EDIT_PROJECT = 'EDIT_PROJECT',
  MANAGE_PROJECT = 'MANAGE_PROJECT',
  MANAGE_PROJECT_MEMBERS = 'MANAGE_PROJECT_MEMBERS',
  MANAGE_PROJECT_SETTINGS = 'MANAGE_PROJECT_SETTINGS',
  ARCHIVE_PROJECT = 'ARCHIVE_PROJECT',
  DELETE_PROJECT = 'DELETE_PROJECT',
  
  // Issue-level permissions
  CREATE_ISSUE = 'CREATE_ISSUE',
  EDIT_ISSUE = 'EDIT_ISSUE',
  DELETE_ISSUE = 'DELETE_ISSUE',
  ARCHIVE_ISSUE = 'ARCHIVE_ISSUE',
  ASSIGN_ISSUE = 'ASSIGN_ISSUE',
  
  // Board-level permissions
  MANAGE_BOARD_STATUS = 'MANAGE_BOARD_STATUS',
  REORDER_BOARD_STATUS = 'REORDER_BOARD_STATUS',
  
  // Comment permissions
  CREATE_COMMENT = 'CREATE_COMMENT',
  EDIT_COMMENT = 'EDIT_COMMENT',
  DELETE_COMMENT = 'DELETE_COMMENT',
  VIEW_COMMENT = 'VIEW_COMMENT',
  
  // Work Log permissions
  CREATE_WORK_LOG = 'CREATE_WORK_LOG',
  EDIT_WORK_LOG = 'EDIT_WORK_LOG',
  DELETE_WORK_LOG = 'DELETE_WORK_LOG',
  VIEW_WORK_LOG = 'VIEW_WORK_LOG',
  EXPORT_WORK_LOG = 'EXPORT_WORK_LOG',
  
  // Global Work Log permissions
  CREATE_GLOBAL_WORK_LOG = 'CREATE_GLOBAL_WORK_LOG',
  EDIT_GLOBAL_WORK_LOG = 'EDIT_GLOBAL_WORK_LOG',
  DELETE_GLOBAL_WORK_LOG = 'DELETE_GLOBAL_WORK_LOG',
  VIEW_GLOBAL_WORK_LOG = 'VIEW_GLOBAL_WORK_LOG',
  VIEW_ALL_GLOBAL_WORK_LOG = 'VIEW_ALL_GLOBAL_WORK_LOG',
  MANAGE_GLOBAL_WORK_LOG = 'MANAGE_GLOBAL_WORK_LOG',
  
  // View permissions
  VIEW_ISSUE = 'VIEW_ISSUE',
  VIEW_BOARD = 'VIEW_BOARD',
  VIEW_ALL_PROJECTS = 'VIEW_ALL_PROJECTS',
  
  // Transition permissions  
  TRANSITION_ISSUE = 'TRANSITION_ISSUE',
  
  // Additional permissions
  VIEW_REPORTS = 'VIEW_REPORTS',
  VIEW_ATTACHMENTS = 'VIEW_ATTACHMENTS',
  UPLOAD_DOCUMENTS = 'UPLOAD_DOCUMENTS',
  VIEW_ACTIVITY_LOGS = 'VIEW_ACTIVITY_LOGS',

  // Custom Field permissions
  MANAGE_CUSTOM_FIELDS = 'MANAGE_CUSTOM_FIELDS',

  // Finance module (granular per-section)
  VIEW_FINANCE_CONTACTS = 'VIEW_FINANCE_CONTACTS',
  MANAGE_FINANCE_CONTACTS = 'MANAGE_FINANCE_CONTACTS',
  DELETE_FINANCE_CONTACTS = 'DELETE_FINANCE_CONTACTS',
  VIEW_FINANCE_UNITS = 'VIEW_FINANCE_UNITS',
  MANAGE_FINANCE_UNITS = 'MANAGE_FINANCE_UNITS',
  DELETE_FINANCE_UNITS = 'DELETE_FINANCE_UNITS',
  VIEW_FINANCE_PARKING = 'VIEW_FINANCE_PARKING',
  MANAGE_FINANCE_PARKING = 'MANAGE_FINANCE_PARKING',
  DELETE_FINANCE_PARKING = 'DELETE_FINANCE_PARKING',
  VIEW_FINANCE_BILLS = 'VIEW_FINANCE_BILLS',
  MANAGE_FINANCE_BILLS = 'MANAGE_FINANCE_BILLS',
  DELETE_FINANCE_BILLS = 'DELETE_FINANCE_BILLS',
  VIEW_FINANCE_RECONCILIATION = 'VIEW_FINANCE_RECONCILIATION',
  MANAGE_FINANCE_RECONCILIATION = 'MANAGE_FINANCE_RECONCILIATION',
  VIEW_FINANCE_SETTINGS = 'VIEW_FINANCE_SETTINGS',
  MANAGE_FINANCE_SETTINGS = 'MANAGE_FINANCE_SETTINGS',
  VIEW_FINANCE_VENDORS = 'VIEW_FINANCE_VENDORS',
  MANAGE_FINANCE_VENDORS = 'MANAGE_FINANCE_VENDORS',
  DELETE_FINANCE_VENDORS = 'DELETE_FINANCE_VENDORS',
}

export enum UserRole {
  SUPER_ADMIN = 'SUPER_ADMIN',
  ADMIN = 'ADMIN',
  USER = 'USER'
}

export enum ProjectRole {
  OWNER = 'OWNER',
  ADMIN = 'ADMIN',
  MEMBER = 'MEMBER',
  VIEWER = 'VIEWER'
}

interface UserPermissions {
  globalPermissions: Permission[]
  projectPermissions: Permission[]
  groupPermissions: Permission[]
  allPermissions: Permission[]
}

interface PermissionCache {
  [key: string]: {
    permissions: UserPermissions
    timestamp: number
  }
}

// Cache duration: 5 minutes
const CACHE_DURATION = 5 * 60 * 1000

// Permission cache
let permissionCache: PermissionCache = {}

/**
 * Custom hook for managing user permissions
 * @param projectId Optional project ID for project-specific permissions
 */
export const usePermissions = (projectId?: string) => {
  const { user } = useAppSelector(state => state.auth)
  const [permissions, setPermissions] = useState<UserPermissions>({
    globalPermissions: [],
    projectPermissions: [],
    groupPermissions: [],
    allPermissions: []
  })
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  // Generate cache key
  // Cache key is now calculated locally in functions to avoid dependency cycles

  // Check if user has a specific permission
  const hasPermission = useMemo(() => 
    (permission: Permission) => {
      // 超級管理員和管理員擁有所有權限
      if (user?.role === UserRole.SUPER_ADMIN || user?.role === UserRole.ADMIN) {
        return true
      }
      return permissions.allPermissions.includes(permission)
    }, [permissions.allPermissions, user?.role]
  )

  // Check multiple permissions at once
  const hasAllPermissions = useMemo(() => 
    (requiredPermissions: Permission[]) => {
      // 超級管理員和管理員擁有所有權限
      if (user?.role === UserRole.SUPER_ADMIN || user?.role === UserRole.ADMIN) {
        return true
      }
      return requiredPermissions.every(permission => 
        permissions.allPermissions.includes(permission)
      )
    }, [permissions.allPermissions, user?.role]
  )

  // Check if user has any of the specified permissions
  const hasAnyPermission = useMemo(() => 
    (requiredPermissions: Permission[]) => {
      // 超級管理員和管理員擁有所有權限
      if (user?.role === UserRole.SUPER_ADMIN || user?.role === UserRole.ADMIN) {
        return true
      }
      return requiredPermissions.some(permission => 
        permissions.allPermissions.includes(permission)
      )
    }, [permissions.allPermissions, user?.role]
  )

  // Check if user is super admin
  const isSuperAdmin = useMemo(() => {
    return user?.role === UserRole.SUPER_ADMIN
  }, [user?.role])

  // Check if user is admin (includes super admin)
  const isAdmin = useMemo(() => {
    return user?.role === UserRole.ADMIN || user?.role === UserRole.SUPER_ADMIN
  }, [user?.role])

  // Check if user has group permissions (has any permissions from groups)
  const hasGroupPermissions = useMemo(() => {
    return permissions.groupPermissions.length > 0
  }, [permissions.groupPermissions])

  // Get permission source info for debugging/display
  const getPermissionSource = useMemo(() => 
    (permission: Permission): string[] => {
      const sources: string[] = []
      
      if (permissions.globalPermissions.includes(permission)) {
        sources.push('角色權限')
      }
      
      if (permissions.projectPermissions.includes(permission)) {
        sources.push('專案權限')
      }
      
      if (permissions.groupPermissions.includes(permission)) {
        sources.push('群組權限')
      }
      
      return sources
    }, [permissions.globalPermissions, permissions.projectPermissions, permissions.groupPermissions]
  )

  // Check permissions via API
  const checkPermissions = async (permissionsList: Permission[]): Promise<Record<string, boolean>> => {
    if (!user) {
      return permissionsList.reduce((acc, perm) => ({ ...acc, [perm]: false }), {})
    }

    // 超級管理員擁有所有權限
    if (user.role === 'SUPER_ADMIN') {
      return permissionsList.reduce((acc, perm) => ({ ...acc, [perm]: true }), {})
    }

    try {
      const response = await fetch(getApiUrl('permission-check/check'), {
        ...createFetchConfig({
          method: 'POST',
          body: JSON.stringify({
            permissions: permissionsList,
            projectId
          })
        })
      })

      if (!response.ok) {
        throw new Error('權限檢查失敗')
      }

      const data = await response.json()
      return data.data
    } catch (err) {
      console.error('權限檢查錯誤:', err)
      return permissionsList.reduce((acc, perm) => ({ ...acc, [perm]: false }), {})
    }
  }

  // Fetch permissions from server
  const fetchPermissions = useCallback(async () => {
    if (!user || !user.id) {
      setPermissions({
        globalPermissions: [],
        projectPermissions: [],
        groupPermissions: [],
        allPermissions: []
      })
      setLoading(false)
      return
    }

    try {
      setLoading(true)
      setError(null)

      // Calculate cache key locally to avoid dependency cycles
      const currentCacheKey = `${user.id}-${projectId || 'global'}`

      // Check cache first
      const cached = permissionCache[currentCacheKey]
      if (cached && Date.now() - cached.timestamp < CACHE_DURATION) {
        setPermissions(cached.permissions)
        setLoading(false)
        return
      }

      const endpoint = projectId 
        ? `users/permissions?projectId=${projectId}`
        : 'users/permissions'

      const response = await fetch(getApiUrl(endpoint), createFetchConfig())

      if (!response.ok) {
        const errorText = await response.text()
        console.error('Permission API Error:', {
          status: response.status,
          statusText: response.statusText,
          body: errorText,
          url: getApiUrl(endpoint)
        })
        throw new Error(`無法載入權限設定 (${response.status}): ${response.statusText}`)
      }

      const data = await response.json()
      const userPermissions: UserPermissions = {
        globalPermissions: data.data?.globalPermissions || [],
        projectPermissions: data.data?.projectPermissions || [],
        groupPermissions: data.data?.groupPermissions || [],
        allPermissions: data.data?.allPermissions || []
      }

      // Update cache
      permissionCache[currentCacheKey] = {
        permissions: userPermissions,
        timestamp: Date.now()
      }

      setPermissions(userPermissions)
    } catch (err: any) {
      console.error('Error fetching permissions:', err)
      setError(err.message || '載入權限失敗')
      
      // Fallback: basic permissions based on role
      const fallbackPermissions = getFallbackPermissions(user.role as UserRole)
      setPermissions(fallbackPermissions)
    } finally {
      setLoading(false)
    }
  }, [user, projectId])

  // Get fallback permissions based on user role
  const getFallbackPermissions = (role: UserRole): UserPermissions => {
    let globalPermissions: Permission[] = []

    switch (role) {
      case UserRole.SUPER_ADMIN:
        globalPermissions = Object.values(Permission)
        break
      case UserRole.ADMIN:
        globalPermissions = [
          Permission.MANAGE_USERS,
          Permission.MANAGE_PROJECT,
          Permission.MANAGE_PROJECT_MEMBERS,
          Permission.CREATE_ISSUE,
          Permission.EDIT_ISSUE,
          Permission.DELETE_ISSUE,
          Permission.ARCHIVE_ISSUE,
          Permission.ASSIGN_ISSUE,
          Permission.MANAGE_BOARD_STATUS,
          Permission.REORDER_BOARD_STATUS,
          Permission.CREATE_COMMENT,
          Permission.EDIT_COMMENT,
          Permission.DELETE_COMMENT
        ]
        break
      case UserRole.USER:
        globalPermissions = [
          Permission.CREATE_ISSUE,
          Permission.EDIT_ISSUE,
          Permission.CREATE_COMMENT,
          Permission.EDIT_COMMENT
        ]
        break
    }

    return {
      globalPermissions,
      projectPermissions: [],
      groupPermissions: [],
      allPermissions: globalPermissions
    }
  }

  // Clear permission cache
  const clearCache = useCallback(() => {
    permissionCache = {}
  }, [])

  // Refresh permissions
  const refreshPermissions = useCallback(() => {
    if (user) {
      const currentCacheKey = `${user.id}-${projectId || 'global'}`
      delete permissionCache[currentCacheKey]
    }
    fetchPermissions()
  }, [user, projectId, fetchPermissions])

  // Fetch permissions when user or project changes
  useEffect(() => {
    fetchPermissions()
  }, [fetchPermissions])

  // Auto-refresh permissions every 10 minutes
  useEffect(() => {
    const interval = setInterval(() => {
      // Clean up old cache entries
      const now = Date.now()
      Object.keys(permissionCache).forEach(key => {
        if (now - permissionCache[key].timestamp > CACHE_DURATION) {
          delete permissionCache[key]
        }
      })
      
      // Refresh current permissions if they're getting stale
      if (user) {
        const currentCacheKey = `${user.id}-${projectId || 'global'}`
        if (permissionCache[currentCacheKey] && 
            now - permissionCache[currentCacheKey].timestamp > CACHE_DURATION / 2) {
          fetchPermissions()
        }
      }
    }, 10 * 60 * 1000) // 10 minutes

    return () => clearInterval(interval)
  }, [user, projectId, fetchPermissions])

  return {
    permissions,
    loading,
    error,
    hasPermission,
    hasAllPermissions,
    hasAnyPermission,
    isSuperAdmin,
    isAdmin,
    hasGroupPermissions,
    getPermissionSource,
    checkPermissions,
    refreshPermissions,
    clearCache
  }
}

export default usePermissions